import { ChevronLeft, ChevronRight, X } from "lucide-mui"
import React, { useMemo, useState } from "react"

import { PlanViewer } from "@/components/plan"
import i18n from "@/i18n"
import Logo from "@/pages/public/logo"

import { type ReservationDetailsStepProps } from "../shared"

import BestPicksSlot from "./best_picks_slot"
import FloorTabs from "./floor_tabs"
import HowItWorksModal from "./how_it_works_modal"
import MobileCoachMark from "./mobile_coach_mark"
import SelectionPills from "./selection_pills"
import { CTA_BORDER, CTA_GRADIENT, MAP_STATE_COLORS, TS_STYLES } from "./styles"
import TableTimeline from "./table_timeline"
import { useMobileTableSelection } from "./use_mobile_table_selection"
import { usePersistedCoachmark } from "./use_persisted_coachmark"

/** Adaptive CTA copy + enablement for the current selection state. */
const getCtaState = (hasTime: boolean, hasTable: boolean, hasConflict: boolean, canContinue: boolean) => {
  // Collision (both set, overlapping): the mock's disabled CTA reads "Choose table to continue"
  // (Figma 1283-7658) — the advisory points the guest at switching the table.
  if (hasTime && hasTable && hasConflict) {
    return { label: i18n.t("app.public.venue.table_selection.choose_table_to_continue"), enabled: false }
  }
  if (hasTable && !hasTime) {
    return { label: i18n.t("app.public.venue.table_selection.choose_time_to_continue"), enabled: false }
  }
  if (!hasTable) {
    return { label: i18n.t("app.public.venue.table_selection.choose_table_to_continue"), enabled: false }
  }
  return { label: i18n.t("app.public.venue.table_selection.continue"), enabled: canContinue }
}

const MobileTableSelection: React.FC<ReservationDetailsStepProps> = ({
  tables,
  openingHours,
  timezone,
  venueSlug,
  venuePlans,
  minAdvanceBookingTime,
  minReservationTime,
  maxReservationTime,
  editMode = false,
  originalReservation,
  onBack,
  onClose,
  onNext,
  onError,
  onShowHowItWorks,
}) => {
  const {
    values,
    hasTime,
    hasTable,
    firstDimension,
    isChecking,
    hasConflicts,
    setHasConflicts,
    isValid,
    handleNext,
    scheduleTimeOptions,
    closedUntil,
    selectedTable,
    selectedTimeRange,
    durationDisplay,
    maxDurationLabel,
    availableCount,
    freeTableIds,
    disabledTables,
    selectedTableBusyBlocks,
    proposedWindow,
    openingRange,
    availableTableIds,
    bestPicks,
    editTime,
    clearTime,
    clearTable,
    selectTable,
    acceptProposed,
    acceptPick,
  } = useMobileTableSelection({
    tables,
    openingHours,
    timezone,
    venueSlug,
    minAdvanceBookingTime,
    minReservationTime,
    maxReservationTime,
    venuePlans,
    editMode,
    originalReservation,
    onNext,
    onError,
  })

  const { coachmarkVisible, dismissCoachmark } = usePersistedCoachmark()
  const [howItWorksOpen, setHowItWorksOpen] = useState(false)

  const openHowItWorks = () => {
    setHowItWorksOpen(true)
    onShowHowItWorks?.()
  }

  const plansWithContent = useMemo(() => venuePlans.filter((plan) => plan.plan), [venuePlans])
  const defaultPlanIndex = useMemo(() => {
    const index = plansWithContent.findIndex((plan) => plan.default)
    return index >= 0 ? index : 0
  }, [plansWithContent])
  const [activePlanIndex, setActivePlanIndex] = useState(defaultPlanIndex)

  const [leftDim, rightDim] = firstDimension === "table" ? (["table", "time"] as const) : (["time", "table"] as const)
  const isEmptyState = !hasTime && !hasTable

  const cta = getCtaState(hasTime, hasTable, hasConflicts, isValid && !isChecking)

  return (
    <div className={TS_STYLES.root} onClickCapture={dismissCoachmark} onTouchStartCapture={dismissCoachmark}>
      <div className={TS_STYLES.header}>
        <button
          type="button"
          onClick={onBack}
          className={TS_STYLES.headerBackBtn}
          aria-label={i18n.t("app.public.venue.modal.back")}
          data-testid="public-reservation-modal-mobile-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className={TS_STYLES.headerTitle}>{i18n.t("app.public.venue.table_selection.title")}</h2>
        <button
          type="button"
          onClick={onClose ?? onBack}
          className={TS_STYLES.headerCloseBtn}
          aria-label={i18n.t("app.public.venue.modal.close")}
          data-testid="public-reservation-modal-mobile-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <FloorTabs
        plans={venuePlans}
        activeIndex={activePlanIndex}
        onSelect={setActivePlanIndex}
        onShowHowItWorks={openHowItWorks}
      />

      <div className={TS_STYLES.mapArea} data-testid="public-reservation-modal-mobile-map">
        <div className="flex min-h-0 flex-1">
          <PlanViewer
            venuePlans={venuePlans}
            tables={tables}
            selectedTableId={values.tableId}
            freeTableIds={hasTable ? [] : freeTableIds}
            disabledTables={disabledTables}
            onTableSelect={selectTable}
            showTableLabels
            hideSectionTabs
            activePlanIndex={activePlanIndex}
            onActivePlanIndexChange={setActivePlanIndex}
            mapControlsPlacement="bottom-left"
            mapControlsSize="lg"
            freeTableColor={MAP_STATE_COLORS.freeTableColor}
            highlightOverlayOpacity={MAP_STATE_COLORS.highlightOverlayOpacity}
            disabledTableTreatment="recolor"
          />
        </div>

        {coachmarkVisible ? <MobileCoachMark onDismiss={dismissCoachmark} /> : null}
      </div>

      <HowItWorksModal open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />

      <div className={TS_STYLES.footer}>
        {hasTable && selectedTable ? (
          <TableTimeline
            tableName={selectedTable.name}
            timeOptions={scheduleTimeOptions}
            busyBlocks={selectedTableBusyBlocks}
            closedUntil={closedUntil}
            currentReservation={hasTime ? { startTime: values.startTime, endTime: values.endTime } : undefined}
            openingRange={openingRange}
            proposedWindow={proposedWindow}
            hasConflict={hasConflicts}
            onConflictChange={setHasConflicts}
            onAcceptProposed={acceptProposed}
            onDeselect={clearTable}
          />
        ) : null}

        <div className={TS_STYLES.footerInner}>
          {isEmptyState ? <BestPicksSlot picks={bestPicks} onAcceptPick={acceptPick} /> : null}

          <SelectionPills
            leftDim={leftDim}
            rightDim={rightDim}
            hasTime={hasTime}
            hasTable={hasTable}
            hasConflict={hasConflicts}
            selectedTimeRange={selectedTimeRange}
            durationLabel={durationDisplay}
            maxDurationLabel={maxDurationLabel}
            tableName={selectedTable?.name ?? null}
            tableSeats={selectedTable?.maxCapacity ?? null}
            availableCount={availableCount}
            tables={tables}
            availableTableIds={availableTableIds}
            timeSlots={scheduleTimeOptions}
            selectedTableBusyBlocks={selectedTableBusyBlocks}
            closedUntil={closedUntil}
            openingRange={openingRange}
            minReservationTime={minReservationTime}
            maxReservationTime={maxReservationTime}
            onClearTime={clearTime}
            onClearTable={clearTable}
            onSelectTable={selectTable}
            onEditTime={editTime}
            startTime={values.startTime}
            endTime={values.endTime}
          />

          <button
            type="button"
            onClick={handleNext}
            disabled={!cta.enabled}
            className={`${TS_STYLES.cta} transition-opacity disabled:opacity-40`}
            style={{ backgroundImage: CTA_GRADIENT, border: CTA_BORDER }}
            data-testid="public-reservation-modal-mobile-cta"
          >
            <span>{cta.label}</span>
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className={TS_STYLES.ctaLogo}>
            <Logo variant="wordmark" className="h-[10px] w-auto" aria-label="tabb" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default MobileTableSelection
